import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Button,
    Card,
    Col,
    Container,
    Form,
    Image,
    InputGroup,
    Row,
} from 'react-bootstrap';
import { Formik } from 'formik';
import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import * as yup from 'yup';

import API from '../../api/axios';

const schema = yup.object({
    name: yup
        .string()
        .required('Required'),

    brandId: yup
        .string()
        .required('Required'),

    type: yup
        .string()
        .required('Required'),

    seats: yup
        .number()
        .min(1)
        .required('Required'),

    engineDisplacement: yup
        .number()
        .min(0)
        .required('Required'),

    horsePower: yup
        .number()
        .min(0)
        .required('Required'),

    height: yup
        .number()
        .min(0)
        .required('Required'),

    width: yup
        .number()
        .min(0)
        .required('Required'),

    finalDriveType: yup
        .string()
        .nullable(true),

    weight: yup
        .number()
        .min(0)
        .nullable(true),

    modelYear: yup
        .number()
        .min(1900)
        .max(2100)
        .nullable(true),

    gears: yup
        .number()
        .min(0)
        .nullable(true),

    cylinders: yup
        .number()
        .min(0)
        .nullable(true),

    engineType: yup
        .string()
        .nullable(true),

    fuelConsumption: yup
        .number()
        .min(0)
        .nullable(true),

    fuelTankCapacity: yup
        .number()
        .min(0)
        .nullable(true),

    seatHeight: yup
        .number()
        .min(0)
        .nullable(true),

    torqueValue: yup
        .number()
        .min(0)
        .nullable(true),

    transmissionType: yup
        .string()
        .nullable(true),

    valvesPerCilynder: yup
        .number()
        .min(0)
        .nullable(true),

    wheelbase: yup
        .number()
        .min(0)
        .nullable(true),

    slug: yup
        .string()
        .nullable(true),

    mainPhotoId: yup
        .string()
        .nullable(true),
});

const EMPTY_ENUMS = {
    type: [],
    transmissionType: [],
    finalDriveType: [],
    engineType: [],
};

const nullsToEmpty = (obj) => {
    if (obj == null) {
        return '';
    }

    if (Array.isArray(obj)) {
        return obj.map(
            nullsToEmpty,
        );
    }

    if (
        typeof obj ===
        'object'
    ) {
        const out = {};

        Object.keys(obj).forEach(
            (key) => {
                out[key] =
                    nullsToEmpty(
                        obj[key],
                    );
            },
        );

        return out;
    }

    return obj;
};

const normalizeEnums = (
    enums,
) => ({
    type:
        enums?.type ||
        [],

    transmissionType:
        enums?.transmissionType ||
        [],

    finalDriveType:
        enums?.finalDriveType ||
        [],

    engineType:
        enums?.engineType ||
        [],
});

const uploadMotorcycleMedia =
    async ({
        file,
        name,
        deleteMediaIds = [],
    }) => {
        const formData =
            new FormData();

        formData.append(
            'file',
            file,
        );

        if (name) {
            formData.append(
                'name',
                name,
            );
        }

        deleteMediaIds.forEach(
            (mediaId) => {
                formData.append(
                    'deleteMediaIds[]',
                    mediaId,
                );
            },
        );

        const {
            data,
        } =
            await API.post(
                '/media/motorcycles',
                formData,
                {
                    headers: {
                        'Content-Type':
                            'multipart/form-data',
                    },
                },
            );

        const mediaId =
            data?.id ||
            data?.data?.id;

        if (!mediaId) {
            throw new Error(
                'Upload succeeded but no media id returned',
            );
        }

        return mediaId;
    };

export default function ModelForm() {
    const { id } =
        useParams();

    const navigate =
        useNavigate();

    const [loading, setLoading] =
        useState(true);

    const [err, setErr] =
        useState('');

    const [
        brandOptions,
        setBrandOptions,
    ] = useState([]);

    const [enums, setEnums] =
        useState(EMPTY_ENUMS);

    const [
        formInit,
        setFormInit,
    ] = useState(null);

    const [
        originalMainPhotoId,
        setOriginalMainPhotoId,
    ] = useState(null);

    const [
        originalPreviewUrl,
        setOriginalPreviewUrl,
    ] = useState(null);

    const [
        previewUrl,
        setPreviewUrl,
    ] = useState(null);

    const [
        newImageFile,
        setNewImageFile,
    ] = useState(null);

    const fileRef =
        useRef(null);

    const previewUrlRef =
        useRef(null);

    const loadModelForm =
        useCallback(async () => {
            if (id) {
                const {
                    data,
                } =
                    await API.get(
                        `/motorcycles/${id}/form`,
                    );

                return {
                    brandOptions:
                        data?.brandOptions ||
                        [],

                    enums:
                        normalizeEnums(
                            data?.enums,
                        ),

                    formInit:
                        nullsToEmpty(
                            data?.values ||
                            {},
                        ),

                    mainPhotoId:
                        data?.values
                            ?.mainPhotoId ||
                        null,

                    previewUrl:
                        data?.readonly
                            ?.Media
                            ?.url ||
                        null,
                };
            }

            const {
                data,
            } =
                await API.get(
                    '/motorcycles/form-meta',
                );

            return {
                brandOptions:
                    data?.brandOptions ||
                    [],

                enums:
                    normalizeEnums(
                        data?.enums,
                    ),

                formInit:
                    nullsToEmpty(
                        data?.defaults ||
                        {},
                    ),

                mainPhotoId:
                    null,

                previewUrl:
                    null,
            };
        }, [id]);

    useEffect(() => {
        let cancelled = false;

        const load =
            async () => {
                try {
                    setLoading(
                        true,
                    );

                    setErr('');

                    const data =
                        await loadModelForm();

                    if (cancelled) {
                        return;
                    }

                    setBrandOptions(
                        data.brandOptions,
                    );

                    setEnums(
                        data.enums,
                    );

                    setFormInit(
                        data.formInit,
                    );

                    setOriginalMainPhotoId(
                        data.mainPhotoId,
                    );

                    setOriginalPreviewUrl(
                        data.previewUrl,
                    );

                    setPreviewUrl(
                        data.previewUrl,
                    );

                    setNewImageFile(
                        null,
                    );
                } catch (error) {
                    if (
                        !cancelled
                    ) {
                        setErr(
                            error?.response
                                ?.data
                                ?.message ||
                            error?.message ||
                            'Failed to load',
                        );
                    }
                } finally {
                    if (
                        !cancelled
                    ) {
                        setLoading(
                            false,
                        );
                    }
                }
            };

        load();

        return () => {
            cancelled = true;
        };
    }, [loadModelForm]);

    useEffect(() => {
        previewUrlRef.current =
            previewUrl;
    }, [previewUrl]);

    useEffect(
        () => () => {
            if (
                previewUrlRef.current
                    ?.startsWith(
                        'blob:',
                    )
            ) {
                URL.revokeObjectURL(
                    previewUrlRef.current,
                );
            }
        },
        [],
    );

    const handlePickImage =
        (event) => {
            const file =
                event.target.files?.[0];

            /*
             * 允许用户重复选择同一个文件。
             */
            event.target.value = '';

            if (!file) {
                return;
            }

            if (
                !file.type.startsWith(
                    'image/',
                )
            ) {
                alert(
                    '请选择图片文件',
                );

                return;
            }

            const nextPreviewUrl =
                URL.createObjectURL(
                    file,
                );

            setPreviewUrl(
                (currentUrl) => {
                    if (
                        currentUrl?.startsWith(
                            'blob:',
                        )
                    ) {
                        URL.revokeObjectURL(
                            currentUrl,
                        );
                    }

                    return nextPreviewUrl;
                },
            );

            setNewImageFile(
                file,
            );
        };

    const handleUndoImage =
        () => {
            setPreviewUrl(
                (currentUrl) => {
                    if (
                        currentUrl?.startsWith(
                            'blob:',
                        )
                    ) {
                        URL.revokeObjectURL(
                            currentUrl,
                        );
                    }

                    return originalPreviewUrl;
                },
            );

            setNewImageFile(
                null,
            );
        };

    const handleSubmit =
        useCallback(
            async (
                values,
                formikHelpers,
            ) => {
                const {
                    setSubmitting,
                } =
                    formikHelpers;

                try {
                    setSubmitting(
                        true,
                    );

                    const oldMainPhotoId =
                        originalMainPhotoId ??
                        null;

                    let mainPhotoId =
                        values.mainPhotoId ||
                        null;

                    let uploadedNewImage =
                        false;

                    if (
                        newImageFile
                    ) {
                        mainPhotoId =
                            await uploadMotorcycleMedia(
                                {
                                    file:
                                        newImageFile,

                                    name:
                                        values?.name,

                                    deleteMediaIds:
                                        oldMainPhotoId
                                            ? [
                                                oldMainPhotoId,
                                            ]
                                            : [],
                                },
                            );

                        uploadedNewImage =
                            true;
                    }

                    const payload = {
                        ...values,
                        mainPhotoId,
                    };

                    if (id) {
                        await API.patch(
                            `/motorcycles/${id}`,
                            payload,
                        );

                        setOriginalMainPhotoId(
                            mainPhotoId,
                        );

                        if (
                            uploadedNewImage
                        ) {
                            /*
                             * 当前 preview 是本地 blob。
                             * 保存成功后先作为新的“当前原图”
                             * 保留显示。
                             *
                             * 下次重新进入页面时，
                             * 后端会返回正式 Media URL。
                             */
                            setOriginalPreviewUrl(
                                previewUrl,
                            );
                        }

                        alert(
                            'Saved',
                        );
                    } else {
                        await API.post(
                            '/motorcycles',
                            payload,
                        );

                        alert(
                            'Created',
                        );
                    }

                    setNewImageFile(
                        null,
                    );
                } catch (error) {
                    alert(
                        error?.response
                            ?.data
                            ?.message ||
                        error?.message ||
                        'Submit failed',
                    );
                } finally {
                    setSubmitting(
                        false,
                    );
                }
            },
            [
                id,
                newImageFile,
                originalMainPhotoId,
                previewUrl,
            ],
        );

    if (loading) {
        return (
            <div className="p-4">
                Loading…
            </div>
        );
    }

    if (err) {
        return (
            <div className="p-4 text-danger">
                {String(err)}
            </div>
        );
    }

    if (!formInit) {
        return null;
    }

    return (
        <Formik
            enableReinitialize
            initialValues={
                formInit
            }
            onSubmit={
                handleSubmit
            }
            validationSchema={
                schema
            }
            validateOnChange={
                false
            }
            validateOnBlur
        >
            {(formik) => (
                <Container
                    fluid
                    className="d-flex flex-column"
                >
                    <Row>
                        <Col
                            sm={12}
                            className="mb-4"
                        >
                            <span className="fw-semibold">
                                {id
                                    ? 'Edit Model'
                                    : 'Create Model'}
                            </span>
                        </Col>

                        {/* 左列：字段 */}
                        <Col md={8}>
                            {/* Basics */}
                            <Card className="mb-3">
                                <Card.Body>
                                    <h6 className="mb-3 text-uppercase text-muted">
                                        Basics
                                    </h6>

                                    <Row className="g-3">
                                        <Col md={8}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Name{' '}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </Form.Label>

                                                <Form.Control
                                                    name="name"
                                                    value={
                                                        formik
                                                            .values
                                                            .name ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    isInvalid={
                                                        !!formik
                                                            .errors
                                                            .name
                                                    }
                                                    placeholder="Model name"
                                                />

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .name
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Seats
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="seats"
                                                        type="number"
                                                        min="1"
                                                        value={
                                                            formik
                                                                .values
                                                                .seats ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        isInvalid={
                                                            !!formik
                                                                .errors
                                                                .seats
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        seats
                                                    </InputGroup.Text>
                                                </InputGroup>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .seats
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Brand{' '}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </Form.Label>

                                                <Form.Select
                                                    name="brandId"
                                                    value={
                                                        formik
                                                            .values
                                                            .brandId ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    isInvalid={
                                                        !!formik
                                                            .errors
                                                            .brandId
                                                    }
                                                >
                                                    <option value="">
                                                        Select
                                                        brand
                                                    </option>

                                                    {brandOptions.map(
                                                        (
                                                            brand,
                                                        ) => (
                                                            <option
                                                                key={
                                                                    brand.id
                                                                }
                                                                value={
                                                                    brand.id
                                                                }
                                                            >
                                                                {
                                                                    brand.name
                                                                }
                                                            </option>
                                                        ),
                                                    )}
                                                </Form.Select>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .brandId
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Type{' '}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </Form.Label>

                                                <Form.Select
                                                    name="type"
                                                    value={
                                                        formik
                                                            .values
                                                            .type ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    isInvalid={
                                                        !!formik
                                                            .errors
                                                            .type
                                                    }
                                                >
                                                    <option value="">
                                                        Select
                                                        type
                                                    </option>

                                                    {(
                                                        enums.type ||
                                                        []
                                                    ).map(
                                                        (
                                                            type,
                                                        ) => (
                                                            <option
                                                                key={
                                                                    type
                                                                }
                                                                value={
                                                                    type
                                                                }
                                                            >
                                                                {type.replaceAll(
                                                                    '_',
                                                                    ' ',
                                                                )}
                                                            </option>
                                                        ),
                                                    )}
                                                </Form.Select>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .type
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Dimensions */}
                            <Card className="mb-3">
                                <Card.Body>
                                    <h6 className="mb-3 text-uppercase text-muted">
                                        Dimensions
                                    </h6>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Height{' '}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="height"
                                                        type="number"
                                                        min="0"
                                                        value={
                                                            formik
                                                                .values
                                                                .height ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        isInvalid={
                                                            !!formik
                                                                .errors
                                                                .height
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        mm
                                                    </InputGroup.Text>
                                                </InputGroup>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .height
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Width{' '}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="width"
                                                        type="number"
                                                        min="0"
                                                        value={
                                                            formik
                                                                .values
                                                                .width ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        isInvalid={
                                                            !!formik
                                                                .errors
                                                                .width
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        mm
                                                    </InputGroup.Text>
                                                </InputGroup>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .width
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Wheelbase
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="wheelbase"
                                                        type="number"
                                                        min="0"
                                                        value={
                                                            formik
                                                                .values
                                                                .wheelbase ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        mm
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Weight
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="weight"
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        value={
                                                            formik
                                                                .values
                                                                .weight ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        kg
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Seat
                                                    Height
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="seatHeight"
                                                        type="number"
                                                        min="0"
                                                        value={
                                                            formik
                                                                .values
                                                                .seatHeight ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        mm
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Model
                                                    Year
                                                </Form.Label>

                                                <Form.Control
                                                    name="modelYear"
                                                    type="number"
                                                    min="1900"
                                                    max="2100"
                                                    value={
                                                        formik
                                                            .values
                                                            .modelYear ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Powertrain */}
                            <Card className="mb-3">
                                <Card.Body>
                                    <h6 className="mb-3 text-uppercase text-muted">
                                        Powertrain
                                    </h6>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Horse
                                                    Power
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="horsePower"
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        value={
                                                            formik
                                                                .values
                                                                .horsePower ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        isInvalid={
                                                            !!formik
                                                                .errors
                                                                .horsePower
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        HP
                                                    </InputGroup.Text>
                                                </InputGroup>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .horsePower
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Engine
                                                    Displacement
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="engineDisplacement"
                                                        type="number"
                                                        min="0"
                                                        value={
                                                            formik
                                                                .values
                                                                .engineDisplacement ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        isInvalid={
                                                            !!formik
                                                                .errors
                                                                .engineDisplacement
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        cc
                                                    </InputGroup.Text>
                                                </InputGroup>

                                                <Form.Control.Feedback
                                                    type="invalid"
                                                >
                                                    {
                                                        formik
                                                            .errors
                                                            .engineDisplacement
                                                    }
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Engine
                                                    Type
                                                </Form.Label>

                                                <Form.Select
                                                    name="engineType"
                                                    value={
                                                        formik
                                                            .values
                                                            .engineType ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                >
                                                    <option value="">
                                                        Select
                                                        engine
                                                        type
                                                    </option>

                                                    {(
                                                        enums.engineType ||
                                                        []
                                                    ).map(
                                                        (
                                                            value,
                                                        ) => (
                                                            <option
                                                                key={
                                                                    value
                                                                }
                                                                value={
                                                                    value
                                                                }
                                                            >
                                                                {value.replaceAll(
                                                                    '_',
                                                                    ' ',
                                                                )}
                                                            </option>
                                                        ),
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Transmission
                                                </Form.Label>

                                                <Form.Select
                                                    name="transmissionType"
                                                    value={
                                                        formik
                                                            .values
                                                            .transmissionType ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                >
                                                    <option value="">
                                                        Select
                                                        transmission
                                                    </option>

                                                    {(
                                                        enums.transmissionType ||
                                                        []
                                                    ).map(
                                                        (
                                                            value,
                                                        ) => (
                                                            <option
                                                                key={
                                                                    value
                                                                }
                                                                value={
                                                                    value
                                                                }
                                                            >
                                                                {value.replaceAll(
                                                                    '_',
                                                                    ' ',
                                                                )}
                                                            </option>
                                                        ),
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Final
                                                    Drive
                                                </Form.Label>

                                                <Form.Select
                                                    name="finalDriveType"
                                                    value={
                                                        formik
                                                            .values
                                                            .finalDriveType ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                >
                                                    <option value="">
                                                        Select
                                                        final
                                                        drive
                                                    </option>

                                                    {(
                                                        enums.finalDriveType ||
                                                        []
                                                    ).map(
                                                        (
                                                            value,
                                                        ) => (
                                                            <option
                                                                key={
                                                                    value
                                                                }
                                                                value={
                                                                    value
                                                                }
                                                            >
                                                                {value.replaceAll(
                                                                    '_',
                                                                    ' ',
                                                                )}
                                                            </option>
                                                        ),
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Valves
                                                    per
                                                    Cylinder
                                                </Form.Label>

                                                <Form.Control
                                                    name="valvesPerCilynder"
                                                    type="number"
                                                    min="0"
                                                    value={
                                                        formik
                                                            .values
                                                            .valvesPerCilynder ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Torque
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="torqueValue"
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        value={
                                                            formik
                                                                .values
                                                                .torqueValue ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        Nm
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Gears
                                                </Form.Label>

                                                <Form.Control
                                                    name="gears"
                                                    type="number"
                                                    min="0"
                                                    value={
                                                        formik
                                                            .values
                                                            .gears ??
                                                        ''
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Fuel & Chassis */}
                            <Card className="mb-3">
                                <Card.Body>
                                    <h6 className="mb-3 text-uppercase text-muted">
                                        Fuel &
                                        Chassis
                                    </h6>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Fuel
                                                    Consumption
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="fuelConsumption"
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        value={
                                                            formik
                                                                .values
                                                                .fuelConsumption ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        L/100km
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Fuel
                                                    Tank
                                                    Capacity
                                                </Form.Label>

                                                <InputGroup>
                                                    <Form.Control
                                                        name="fuelTankCapacity"
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        value={
                                                            formik
                                                                .values
                                                                .fuelTankCapacity ??
                                                            ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />

                                                    <InputGroup.Text>
                                                        L
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* 右列：图片 */}
                        <Col md={4}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column align-items-center">
                                    <h6 className="mb-3 text-uppercase text-muted align-self-start">
                                        Main Photo
                                    </h6>

                                    {previewUrl ? (
                                        <>
                                            <Image
                                                src={
                                                    previewUrl
                                                }
                                                fluid
                                                thumbnail
                                                className="mb-3"
                                                style={{
                                                    maxHeight:
                                                        260,
                                                    objectFit:
                                                        'contain',
                                                    width:
                                                        '100%',
                                                }}
                                            />

                                            <div className="d-grid gap-2 w-100">
                                                <Button
                                                    type="button"
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() =>
                                                        fileRef
                                                            .current
                                                            ?.click()
                                                    }
                                                >
                                                    {newImageFile
                                                        ? 'Change image'
                                                        : 'Upload new image'}
                                                </Button>

                                                {newImageFile && (
                                                    <Button
                                                        type="button"
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={
                                                            handleUndoImage
                                                        }
                                                    >
                                                        Undo New
                                                        Image
                                                    </Button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center flex-grow-1 d-flex flex-column justify-content-center w-100">
                                            <div className="text-muted mb-2">
                                                No
                                                image
                                            </div>

                                            <Button
                                                type="button"
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() =>
                                                    fileRef
                                                        .current
                                                        ?.click()
                                                }
                                            >
                                                Upload
                                                Image
                                            </Button>
                                        </div>
                                    )}

                                    <input
                                        ref={
                                            fileRef
                                        }
                                        id="imageUpload"
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={
                                            handlePickImage
                                        }
                                    />
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* 操作条 */}
                        <Col
                            xs={12}
                            className="mt-2"
                        >
                            <Card className="border-0">
                                <Card.Body className="d-flex justify-content-end gap-2">
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={() =>
                                            navigate(
                                                '/models',
                                            )
                                        }
                                        disabled={
                                            formik.isSubmitting ||
                                            loading
                                        }
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        type="button"
                                        onClick={
                                            formik.handleSubmit
                                        }
                                        className="btn-custom-success"
                                        disabled={
                                            formik.isSubmitting ||
                                            loading
                                        }
                                    >
                                        {formik.isSubmitting
                                            ? 'Saving…'
                                            : id
                                                ? 'Save changes'
                                                : 'Save'}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            )}
        </Formik>
    );
}